import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Archive, FileText, MoreVertical, Plus } from "lucide-react";
import { cn } from "@/cn";
import type { DocumentAsset } from "@/api/documents";
import type { DocumentDocType } from "@/hooks/useDocuments";
import {
  useArchiveDocument,
  useDocuments,
  useDownloadDocument,
  useUpdateDocument,
  useUploadDocument,
} from "@/hooks/useDocuments";
import { Modal } from "@/components/ui/modal";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
  type MetadataItem,
} from "@/components/collection";

const ALLOWED_MODES = ["cards", "list", "table"] as const;

type FolderTab = "library" | "archive";

const DOC_TYPE_OPTIONS: { value: DocumentDocType | "all"; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "cv", label: "CV / Resume" },
  { value: "cover_letter", label: "Cover letter" },
  { value: "portfolio", label: "Portfolio" },
  { value: "company_research", label: "Company research" },
  { value: "other", label: "Other" },
];

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "updatedAt", order: "desc", label: "Recently updated" },
  { field: "createdAt", order: "desc", label: "Newest" },
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "size", order: "desc", label: "Largest" },
  { field: "docType", order: "asc", label: "Type" },
];

const DOC_TYPE_LABEL: Record<string, string> = {
  cv: "CV / Resume",
  cover_letter: "Cover letter",
  portfolio: "Portfolio",
  company_research: "Company research",
  other: "Other",
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function docTitle(doc: DocumentAsset): string {
  return doc.originalFileName || doc.fileName || "Untitled";
}

function docTypeLabel(docType: string): string {
  return DOC_TYPE_LABEL[docType] ?? docType;
}

function isArchived(doc: DocumentAsset): boolean {
  return doc.isActive === false;
}

function stop(e: MouseEvent) {
  e.stopPropagation();
}

function applySort(
  items: DocumentAsset[],
  sort: EntitySortOption,
): DocumentAsset[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "name") {
      return docTitle(a).localeCompare(docTitle(b)) * dir;
    }
    if (sort.field === "size") {
      return (a.fileSize - b.fileSize) * dir;
    }
    if (sort.field === "docType") {
      return a.docType.localeCompare(b.docType) * dir;
    }
    if (sort.field === "createdAt") {
      return (Date.parse(a.createdAt) - Date.parse(b.createdAt)) * dir;
    }
    return (Date.parse(a.updatedAt) - Date.parse(b.updatedAt)) * dir;
  });
}

function AssetMenu({
  doc,
  busy,
  onDownload,
  onSetDefault,
  onArchive,
  onRestore,
}: {
  doc: DocumentAsset;
  busy: boolean;
  onDownload: () => void;
  onSetDefault: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const archived = isArchived(doc);

  const updateMenuPos = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 160;
    const gap = 4;
    const left = Math.min(
      Math.max(8, rect.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    );
    // Prefer below the button; flip above if near the bottom of the viewport.
    const below = rect.bottom + gap;
    const estimatedHeight = 120;
    const top =
      below + estimatedHeight > window.innerHeight - 8
        ? Math.max(8, rect.top - estimatedHeight - gap)
        : below;
    setMenuPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReposition = () => updateMenuPos();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: menuPos.top, left: menuPos.left }}
            className="fixed z-[80] min-w-[10rem] rounded-md border border-border bg-surface py-1 shadow-lg"
            onClick={stop}
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted/20"
              onClick={() => {
                setOpen(false);
                onDownload();
              }}
            >
              Download
            </button>
            {!archived && doc.docType === "cv" && !doc.isDefault && (
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted/20"
                onClick={() => {
                  setOpen(false);
                  onSetDefault();
                }}
              >
                Set as default
              </button>
            )}
            {archived ? (
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted/20"
                onClick={() => {
                  setOpen(false);
                  onRestore();
                }}
              >
                Restore to library
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-1.5 text-left text-xs text-destructive hover:bg-destructive/10"
                onClick={() => {
                  setOpen(false);
                  onArchive();
                }}
              >
                Archive
              </button>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative" onClick={stop}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted/20 hover:text-foreground disabled:opacity-50"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {menu}
    </div>
  );
}

export function AssetsPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [folder, setFolder] = useState<FolderTab>("library");
  const [searchQuery, setSearchQuery] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState<string>("all");
  const [uploadDocType, setUploadDocType] = useState<DocumentDocType>("cv");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);
  const [status, setStatus] = useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = useState<DocumentAsset | null>(
    null,
  );

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_assets",
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const { data: documents = [], isLoading, error } = useDocuments({
    includeArchived: true,
    ...(docTypeFilter === "all" ? {} : { docType: docTypeFilter }),
  });
  const upload = useUploadDocument();
  const updateDoc = useUpdateDocument();
  const archive = useArchiveDocument();
  const download = useDownloadDocument();
  const busy =
    upload.isPending ||
    updateDoc.isPending ||
    archive.isPending ||
    download.isPending;

  const libraryCount = useMemo(
    () => documents.filter((d) => !isArchived(d)).length,
    [documents],
  );
  const archiveCount = useMemo(
    () => documents.filter((d) => isArchived(d)).length,
    [documents],
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let items = documents.filter((d) =>
      folder === "archive" ? isArchived(d) : !isArchived(d),
    );
    if (q) {
      items = items.filter((d) => {
        const hay = [
          d.originalFileName,
          d.fileName,
          d.docType,
          d.mimeType,
          ...(d.tags ?? []),
          d.linkedCompanyId,
          d.linkedJobId,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return applySort(items, sortOption);
  }, [documents, searchQuery, sortOption, folder]);

  const getSortValue = (doc: DocumentAsset, key: string) => {
    if (key === "name") return docTitle(doc);
    if (key === "docType") return doc.docType;
    if (key === "size") return doc.fileSize;
    if (key === "updatedAt") return doc.updatedAt;
    if (key === "version") return doc.version ?? 0;
    return "";
  };

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: filtered,
      defaultKey: null,
      getValue: getSortValue,
    });

  const filterFields: EntityFilterSelectField[] = [
    {
      id: "docType",
      label: "Type",
      value: docTypeFilter,
      allLabel: "All types",
      options: DOC_TYPE_OPTIONS.filter((o) => o.value !== "all").map((o) => ({
        value: o.value,
        label: o.label,
      })),
      onChange: setDocTypeFilter,
    },
  ];

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setStatus(null);
    try {
      await upload.mutateAsync({
        file,
        docType: uploadDocType,
        isDefault: uploadDocType === "cv",
      });
      setFolder("library");
      setStatus("Uploaded");
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function confirmArchive() {
    if (!pendingArchive) return;
    try {
      await archive.mutateAsync(pendingArchive.id);
      setPendingArchive(null);
      setStatus("Moved to Archive");
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Archive failed");
    }
  }

  function menuFor(doc: DocumentAsset) {
    return (
      <AssetMenu
        doc={doc}
        busy={busy}
        onDownload={() => void download.mutateAsync(doc.id)}
        onSetDefault={() =>
          void updateDoc.mutateAsync({
            docId: doc.id,
            patch: { isDefault: true },
          })
        }
        onArchive={() => setPendingArchive(doc)}
        onRestore={() =>
          void updateDoc
            .mutateAsync({
              docId: doc.id,
              patch: { isActive: true },
            })
            .then(() => {
              setStatus("Restored to library");
              setTimeout(() => setStatus(null), 2000);
            })
        }
      />
    );
  }

  function metadataFor(doc: DocumentAsset): MetadataItem[] {
    const items: MetadataItem[] = [
      { value: formatBytes(doc.fileSize) },
      { value: `v${doc.version ?? 1}` },
      {
        value: formatWhen(
          folder === "archive" ? doc.archivedAt ?? doc.updatedAt : doc.updatedAt,
        ),
      },
    ];
    if (doc.mimeType) {
      items.push({ value: doc.mimeType.split("/").pop() ?? doc.mimeType });
    }
    return items;
  }

  function descriptionFor(doc: DocumentAsset): string {
    const parts: string[] = [];
    if (doc.linkedCompanyId) parts.push(`Company: ${doc.linkedCompanyId}`);
    if (doc.linkedJobId) parts.push(`Job: ${doc.linkedJobId}`);
    if (doc.tags?.length) parts.push(`Tags: ${doc.tags.join(", ")}`);
    if (doc.processingStatus && doc.processingStatus !== "completed") {
      parts.push(`Status: ${doc.processingStatus}`);
    }
    return parts.join(" · ");
  }

  const columns: Column<DocumentAsset>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (doc) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">
            {docTitle(doc)}
          </div>
          {doc.isDefault && (
            <span className="text-[10px] font-medium uppercase text-brand">
              Default CV
            </span>
          )}
        </div>
      ),
    },
    {
      key: "docType",
      header: "Type",
      sortable: true,
      render: (doc) => docTypeLabel(doc.docType),
    },
    {
      key: "size",
      header: "Size",
      sortable: true,
      render: (doc) => formatBytes(doc.fileSize),
    },
    {
      key: "version",
      header: "Ver",
      sortable: true,
      render: (doc) => `v${doc.version ?? 1}`,
    },
    {
      key: "updatedAt",
      header: folder === "archive" ? "Archived" : "Updated",
      sortable: true,
      render: (doc) =>
        formatWhen(
          folder === "archive" ? doc.archivedAt ?? doc.updatedAt : doc.updatedAt,
        ),
    },
    {
      key: "actions",
      header: "",
      className: "w-12",
      render: (doc) => menuFor(doc),
    },
  ];

  function tabButton(id: FolderTab, label: string, count: number) {
    const active = folder === id;
    return (
      <button
        type="button"
        onClick={() => setFolder(id)}
        className={cn(
          "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition",
          active
            ? "bg-brand/15 text-brand"
            : "text-muted-foreground hover:bg-muted/20 hover:text-foreground",
        )}
      >
        {id === "archive" ? (
          <Archive className="h-3.5 w-3.5" />
        ) : (
          <FileText className="h-3.5 w-3.5" />
        )}
        {label}
        <span className="text-[10px] opacity-70">{count}</span>
      </button>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Assets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            File attachments for Apply and job sends (PDF/DOCX). The Markdown CV
            that Ask AI reads lives in{" "}
            <Link
              to="/profile/ai-context"
              className="text-brand hover:underline"
            >
              AI Context
            </Link>
            .
          </p>
        </div>
        {folder === "library" && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={uploadDocType}
              onChange={(e) =>
                setUploadDocType(e.target.value as DocumentDocType)
              }
              className="rounded-md border border-border bg-card px-2 py-2 text-xs text-foreground"
              aria-label="Upload document type"
            >
              {DOC_TYPE_OPTIONS.filter((o) => o.value !== "all").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {upload.isPending ? "Uploading…" : "Upload"}
            </button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.md,.txt,.png,.jpg,.jpeg,.webp"
              onChange={(e) => void handleFileChange(e)}
            />
          </div>
        )}
      </header>

      <div className="flex gap-1 rounded-lg border border-border p-1 w-fit">
        {tabButton("library", "Library", libraryCount)}
        {tabButton("archive", "Archive", archiveCount)}
      </div>

      {status && (
        <p
          className={cn(
            "text-xs",
            status.toLowerCase().includes("fail")
              ? "text-destructive"
              : "text-success",
          )}
        >
          {status}
        </p>
      )}

      <CollectionCommandBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by filename, type, tags…"
        sortOption={sortOption}
        onSortChange={setSortOption}
        sortOptions={SORT_OPTIONS}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        allowedModes={allowedModes}
        filterFields={filterFields}
        activeFilterCount={docTypeFilter !== "all" ? 1 : 0}
        onClear={() => {
          setSearchQuery("");
          setDocTypeFilter("all");
        }}
        primaryAction={
          folder === "library"
            ? {
                label: "Upload",
                icon: <Plus className="h-4 w-4" />,
                onClick: () => fileRef.current?.click(),
              }
            : undefined
        }
      />

      <p className="text-xs text-muted-foreground">
        {filtered.length}{" "}
        {folder === "archive" ? "archived" : "library"} asset
        {filtered.length === 1 ? "" : "s"}
      </p>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">Failed to load assets.</p>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
          {folder === "archive" ? (
            <>
              <Archive className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                Archive is empty
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Archived files stay here so they don’t disappear from your
                library by accident.
              </p>
            </>
          ) : (
            <>
              <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">
                No assets yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Upload a PDF resume for Apply, or edit the AI-readable Markdown
                CV in AI Context.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setUploadDocType("cv");
                    fileRef.current?.click();
                  }}
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90"
                >
                  Upload CV PDF
                </button>
                <Link
                  to="/profile/ai-context"
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/20"
                >
                  Open AI Context
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <EntityListLayout
          viewMode={viewMode}
          cards={
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((doc) => (
                <EntityCard
                  key={doc.id}
                  id={doc.id}
                  title={docTitle(doc)}
                  description={descriptionFor(doc) || undefined}
                  primaryBadge={{
                    label: docTypeLabel(doc.docType),
                    colorClass: "bg-muted/40 text-muted-foreground",
                  }}
                  secondaryBadge={
                    doc.isDefault
                      ? {
                          label: "Default",
                          colorClass: "bg-brand/15 text-brand",
                        }
                      : folder === "archive"
                        ? {
                            label: "Archived",
                            colorClass: "bg-muted/40 text-muted-foreground",
                          }
                        : doc.processingStatus &&
                            doc.processingStatus !== "completed"
                          ? {
                              label: doc.processingStatus,
                              colorClass: "bg-warning/15 text-warning",
                            }
                          : undefined
                  }
                  icon={
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  }
                  metadata={metadataFor(doc)}
                  actions={menuFor(doc)}
                />
              ))}
            </div>
          }
          list={
            <div className="space-y-2">
              {filtered.map((doc) => (
                <EntityCompactItem
                  key={doc.id}
                  id={doc.id}
                  title={docTitle(doc)}
                  description={descriptionFor(doc) || formatBytes(doc.fileSize)}
                  badges={[
                    {
                      label: docTypeLabel(doc.docType),
                      colorClass: "bg-muted/40 text-muted-foreground",
                    },
                    ...(doc.isDefault
                      ? [
                          {
                            label: "Default",
                            colorClass: "bg-brand/15 text-brand",
                          },
                        ]
                      : []),
                  ]}
                  metadata={metadataFor(doc)}
                  actions={menuFor(doc)}
                />
              ))}
            </div>
          }
          table={
            <DataTable
              data={sortedItems}
              columns={columns}
              getRowId={(doc) => doc.id}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          }
        />
      )}

      <Modal
        open={Boolean(pendingArchive)}
        onClose={() => setPendingArchive(null)}
        labelledBy="archive-confirm-title"
        panelClassName="max-w-md"
      >
        <div className="border-b border-border px-4 py-3">
          <h2
            id="archive-confirm-title"
            className="text-sm font-semibold text-foreground"
          >
            Archive document?
          </h2>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-muted-foreground">
          <p>Are you sure you want to archive this document?</p>
          {pendingArchive && (
            <p className="rounded-md border border-border bg-bg/40 px-3 py-2 text-foreground">
              {docTitle(pendingArchive)}
            </p>
          )}
          <p className="text-xs">
            It will move to the Archive folder — you can restore it later.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={() => setPendingArchive(null)}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/20"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={archive.isPending}
            onClick={() => void confirmArchive()}
            className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {archive.isPending ? "Archiving…" : "Archive"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
